/**
 * WalletConnect via @walletconnect/ethereum-provider with display_uri for in-app QR
 * (same pattern as ChargeAndStrike apps/web-app).
 *
 * Multiple wagmi connectors may share one WalletConnect Cloud `projectId` (e.g. separate
 * WalletConnect vs mOne UI entries). WalletConnect Core allows only one
 * `EthereumProvider.init` per process for a given project, so we cache one provider
 * per `projectId` and fan out provider events to every connector subscription.
 */

import { createConnector } from 'wagmi'
import { getAddress } from 'viem'

type EthereumProviderInstance = InstanceType<
  (typeof import('@walletconnect/ethereum-provider'))['EthereumProvider']
>

type WagmiEmitter = {
  emit: (event: string, ...args: unknown[]) => void
}

type ProjectCache = {
  provider: EthereumProviderInstance | null
  providerPromise: Promise<EthereumProviderInstance> | null
  chainListenersAttached: boolean
  optionalChains: number[]
  rpcMap: Record<number, string>
  displayUriCallbacks: Set<(uri: string) => void>
  chainSubscriptions: Set<WagmiEmitter>
}

const projectCaches = new Map<string, ProjectCache>()

function getProjectCache(
  projectId: string,
  optionalChains: number[],
  rpcMap: Record<number, string>,
  onDisplayUri?: (uri: string) => void,
): ProjectCache {
  let cache = projectCaches.get(projectId)
  if (!cache) {
    const displayUriCallbacks = new Set<(uri: string) => void>()
    if (onDisplayUri) displayUriCallbacks.add(onDisplayUri)
    cache = {
      provider: null,
      providerPromise: null,
      chainListenersAttached: false,
      optionalChains,
      rpcMap,
      displayUriCallbacks,
      chainSubscriptions: new Set(),
    }
    projectCaches.set(projectId, cache)
  } else if (onDisplayUri) {
    cache.displayUriCallbacks.add(onDisplayUri)
  }
  return cache
}

function attachSharedChainListeners(p: EthereumProviderInstance, cache: ProjectCache) {
  if (cache.chainListenersAttached) return
  cache.chainListenersAttached = true

  p.on('connect', (connectInfo: { chainId: string }) => {
    const chainId = Number(connectInfo.chainId)
    const accounts = p.accounts.map((a: string) => getAddress(a))
    for (const emitter of cache.chainSubscriptions) {
      emitter.emit('connect', { accounts, chainId })
    }
  })
  p.on('disconnect', () => {
    for (const emitter of cache.chainSubscriptions) {
      emitter.emit('disconnect')
    }
  })
  p.on('chainChanged', (cid: string) => {
    for (const emitter of cache.chainSubscriptions) {
      emitter.emit('change', { chainId: Number(cid) })
    }
  })
  p.on('accountsChanged', (accounts: string[]) => {
    for (const emitter of cache.chainSubscriptions) {
      if (accounts.length === 0) emitter.emit('disconnect')
      else emitter.emit('change', { accounts: accounts.map((a: string) => getAddress(a)) })
    }
  })
}

export type WalletConnectWithUriParameters = {
  projectId: string
  optionalChains: number[]
  rpcMap: Record<number, string>
  onDisplayUri?: (uri: string) => void
  /** Distinct id when using a second Cloud project (e.g. mOne). */
  connectorId?: string
  connectorName?: string
}

export function normalizeWalletConnectRpcMap(
  rpcMap: Record<number, string>,
  origin = typeof window !== 'undefined' ? window.location.origin : undefined,
): Record<number, string> {
  const normalizedEntries = Object.entries(rpcMap).map(([chainId, rpcUrl]) => {
    if (!rpcUrl.startsWith('/') || !origin) {
      return [Number(chainId), rpcUrl] as const
    }
    return [Number(chainId), new URL(rpcUrl, origin).toString()] as const
  })

  return Object.fromEntries(normalizedEntries)
}

export function walletConnectWithUri(parameters: WalletConnectWithUriParameters) {
  const {
    projectId,
    optionalChains,
    rpcMap,
    onDisplayUri,
    connectorId = 'walletConnect',
    connectorName = 'WalletConnect',
  } = parameters

  const cache = getProjectCache(projectId, optionalChains, rpcMap, onDisplayUri)

  function getProviderPromise(): Promise<EthereumProviderInstance> {
    if (cache.provider) return Promise.resolve(cache.provider)
    if (cache.providerPromise) return cache.providerPromise
    cache.providerPromise = (async () => {
      const { EthereumProvider } = await import('@walletconnect/ethereum-provider')
      const oneOrMoreChains: [number, ...number[]] =
        cache.optionalChains.length > 0
          ? [cache.optionalChains[0]!, ...cache.optionalChains.slice(1)]
          : [1]
      const p = await EthereumProvider.init({
        projectId,
        optionalChains: oneOrMoreChains,
        rpcMap: normalizeWalletConnectRpcMap(cache.rpcMap),
        showQrModal: false,
        disableProviderPing: true,
      })
      p.on('display_uri', (uri: string) => {
        for (const cb of cache.displayUriCallbacks) cb(uri)
      })
      cache.provider = p
      return p
    })()
    return cache.providerPromise
  }

  function resetSharedProvider() {
    cache.provider = null
    cache.providerPromise = null
    cache.chainListenersAttached = false
  }

  // @ts-expect-error wagmi createConnector generic vs our runtime shape
  return createConnector((config) => {
    // Two structurally unrelated emitter shapes: wagmi's typed ConnectorEventMap
    // and our runtime-shaped one. The double cast is what TypeScript itself
    // prescribes here, and it is deliberate rather than a silenced error.
    const emitter = config.emitter as unknown as WagmiEmitter
    cache.chainSubscriptions.add(emitter)

    return {
      id: connectorId,
      name: connectorName,
      type: 'walletConnect' as const,
      async connect({ chainId } = {}) {
        const p = await getProviderPromise()
        attachSharedChainListeners(p, cache)
        const targetChainId = chainId ?? config.chains[0]?.id
        if (!targetChainId) throw new Error('No chains found on connector.')

        const optionalChainsList = config.chains.filter((c) => c.id !== targetChainId).map((c) => c.id)
        const hasActiveSession = Boolean(p.session)
        const hasAccounts = Array.isArray(p.accounts) && p.accounts.length > 0
        const requiresReconnect = !hasActiveSession || !hasAccounts || p.chainId !== targetChainId

        if (requiresReconnect && hasActiveSession && p.chainId !== targetChainId) {
          await p.disconnect().catch(() => {})
        }

        if (requiresReconnect) {
          const connectedAccounts = await p.connect({
            optionalChains: [targetChainId, ...optionalChainsList],
          })
          const accountsSource =
            Array.isArray(connectedAccounts) && connectedAccounts.length > 0
              ? connectedAccounts
              : p.accounts
          const accounts = accountsSource.map((a: string) => getAddress(a))
          return { accounts, chainId: p.chainId ?? targetChainId }
        }

        return {
          accounts: p.accounts.map((a: string) => getAddress(a)),
          chainId: p.chainId ?? targetChainId,
        }
      },
      async disconnect() {
        const p = await getProviderPromise().catch(() => null)
        if (p) await p.disconnect().catch(() => {})
        resetSharedProvider()
      },
      async getAccounts() {
        const p = await getProviderPromise()
        return p.accounts.map((a: string) => getAddress(a))
      },
      async getChainId() {
        const p = await getProviderPromise()
        return p.chainId
      },
      async getProvider() {
        return getProviderPromise()
      },
      async isAuthorized() {
        try {
          const p = await getProviderPromise()
          return p.accounts.length > 0
        } catch {
          return false
        }
      },
      onAccountsChanged(accounts: string[]) {
        if (accounts.length === 0) config.emitter.emit('disconnect')
        else
          config.emitter.emit('change', {
            accounts: accounts.map((a: string) => getAddress(a)),
          })
      },
      onChainChanged(chainId: string) {
        config.emitter.emit('change', { chainId: Number(chainId) })
      },
      onConnect(connectInfo: { chainId: string }) {
        const chainId = Number(connectInfo.chainId)
        void getProviderPromise()
          .then((p) => {
            const accounts = p.accounts.map((a: string) => getAddress(a))
            config.emitter.emit('connect', { accounts, chainId })
          })
          .catch(() => {
            config.emitter.emit('disconnect')
          })
      },
      onDisconnect() {
        config.emitter.emit('disconnect')
      },
    }
  })
}
